/**
 * Phase 3.A — vault-wide receipt browser + rejection detail.
 *
 * Lives next to `/agents/verification` (does not replace the queue or the
 * #45973 hero). Binding is first-class: a dirty receipt proves less than a
 * staged one. Never a bare checkmark.
 */
import * as React from "react";
import { RouterLink as Link, useNavigate } from "@excellent/ui/router";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  StatCard,
  formatDateTime,
  formatRelativeTime,
  type DataTableColumn,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  evidence,
  type EvidenceBrowseReceiptDto,
  type EvidenceRejectBrowseRowDto,
  type EvidenceRejectDetailDto,
  type EvidenceRejectNextKind,
} from "@excellent/ui/api/contracts/evidence";

const MUTED = "type-subtext text-muted-foreground";
const NUM = "tabular-nums";

function asIso(s: string): string {
  const t = s.trim();
  if (!t) return t;
  const normalized = t.includes("T") ? t : t.replace(" ", "T");
  return /[Zz]|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`;
}

function shortHash(h: string | null | undefined, n = 12): string {
  if (!h) return "—";
  return h.length <= n ? h : `${h.slice(0, n)}…`;
}

export function BindingBadge({ binding }: { binding: string | null | undefined }) {
  if (binding === "staged") {
    return <IntentBadge intent="success">staged</IntentBadge>;
  }
  if (binding === "dirty") {
    return <IntentBadge intent="warning">dirty</IntentBadge>;
  }
  return <span className={MUTED}>{binding ?? "—"}</span>;
}

function nextIntent(kind: EvidenceRejectNextKind): "danger" | "success" | "info" | "warning" {
  if (kind === "standing") return "danger";
  if (kind === "reworked") return "success";
  if (kind === "superseded") return "info";
  return "warning";
}

function nextLabel(kind: EvidenceRejectNextKind): string {
  if (kind === "standing") return "standing";
  if (kind === "reworked") return "reworked";
  if (kind === "superseded") return "superseded";
  return "later reject";
}

export type BrowseSearch = {
  tab?: "receipts" | "rejects";
  binding?: "staged" | "dirty";
  exit?: "pass" | "fail";
  standing?: "yes" | "no";
  reject?: number;
  q?: string;
};

export function browseHref(next: BrowseSearch): string {
  const p = new URLSearchParams();
  if (next.tab) p.set("tab", next.tab);
  if (next.binding) p.set("binding", next.binding);
  if (next.exit) p.set("exit", next.exit);
  if (next.standing) p.set("standing", next.standing);
  if (next.reject) p.set("reject", String(next.reject));
  if (next.q) p.set("q", next.q);
  const qs = p.toString();
  return qs ? `/agents/verification?${qs}` : "/agents/verification";
}

function FilterChip({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button asChild variant={active ? "default" : "outline"} size="sm">
      <Link href={href}>{children}</Link>
    </Button>
  );
}

export function ReceiptsBrowser({ search }: { search: BrowseSearch }) {
  const { data, error, isLoading } = useApi(evidence.receiptBrowse, {
    query: {
      limit: 100,
      binding: search.binding,
      exit: search.exit,
      q: search.q || undefined,
    },
  });
  const receipts = data?.receipts ?? [];
  const summary = data?.summary;

  const columns: DataTableColumn<EvidenceBrowseReceiptDto>[] = [
    {
      key: "task",
      header: "Task",
      sort: (a, b) => a.taskId - b.taskId,
      render: (r) => (
        <Link
          href={`/agents/verification/${r.taskId}`}
          className="text-left text-foreground hover:underline"
        >
          <span className={NUM}>#{r.taskId}</span>{" "}
          <span className="line-clamp-1">{r.taskTitle}</span>
        </Link>
      ),
    },
    {
      key: "command",
      header: "Gate",
      sort: (a, b) => a.command.localeCompare(b.command),
      render: (r) => <code className="type-subtext">{r.command}</code>,
    },
    {
      key: "exit",
      header: "Exit",
      sort: (a, b) => a.exitCode - b.exitCode,
      render: (r) => (
        <IntentBadge intent={r.exitCode === 0 ? "success" : "danger"}>
          {r.exitCode === 0 ? "pass" : `fail ${r.exitCode}`}
        </IntentBadge>
      ),
    },
    {
      key: "binding",
      header: "Binding",
      sort: (a, b) => (a.binding ?? "").localeCompare(b.binding ?? ""),
      render: (r) => <BindingBadge binding={r.binding} />,
    },
    {
      key: "env",
      header: "Env policy",
      render: (r) => <span className={MUTED}>{r.envPolicy ?? "—"}</span>,
    },
    {
      key: "tree",
      header: "Tree hash",
      render: (r) => (
        <code className={MUTED} title={r.treeHash}>
          {shortHash(r.treeHash, 12)}
        </code>
      ),
    },
    {
      key: "when",
      header: "When",
      sort: (a, b) => a.createdAt.localeCompare(b.createdAt),
      render: (r) => (
        <span className={MUTED}>{formatRelativeTime(asIso(r.createdAt))}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-4">
        <StatCard title="Receipts" value={String(summary?.total ?? "…")} />
        <StatCard
          title="Staged"
          value={String(summary?.staged ?? "…")}
          description="Binding proves the tree that ran"
          leadingDot="success"
        />
        <StatCard
          title="Dirty"
          value={String(summary?.dirty ?? "…")}
          description="Uncommitted work — proves less"
          leadingDot="warning"
        />
        <StatCard
          title="Failing"
          value={String(summary?.failing ?? "…")}
          leadingDot={(summary?.failing ?? 0) > 0 ? "danger" : "neutral"}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          href={browseHref({ tab: "receipts", exit: search.exit, q: search.q })}
          active={!search.binding}
        >
          All bindings
        </FilterChip>
        <FilterChip
          href={browseHref({ tab: "receipts", binding: "staged", exit: search.exit, q: search.q })}
          active={search.binding === "staged"}
        >
          Staged
        </FilterChip>
        <FilterChip
          href={browseHref({ tab: "receipts", binding: "dirty", exit: search.exit, q: search.q })}
          active={search.binding === "dirty"}
        >
          Dirty
        </FilterChip>
        <FilterChip
          href={browseHref({ tab: "receipts", binding: search.binding, q: search.q })}
          active={!search.exit}
        >
          Any exit
        </FilterChip>
        <FilterChip
          href={browseHref({ tab: "receipts", binding: search.binding, exit: "pass", q: search.q })}
          active={search.exit === "pass"}
        >
          Pass
        </FilterChip>
        <FilterChip
          href={browseHref({ tab: "receipts", binding: search.binding, exit: "fail", q: search.q })}
          active={search.exit === "fail"}
        >
          Fail
        </FilterChip>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Gate receipts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && !data ? (
            <p className="type-body text-muted-foreground">Loading vault receipts…</p>
          ) : error ? (
            <p className="type-body text-destructive">
              {error instanceof Error ? error.message : String(error)}
            </p>
          ) : (
            <DataTable
              rows={receipts}
              columns={columns}
              rowKey={(r) => r.uid ?? r.id}
              wrapInCard={false}
              empty="No gate receipts match these filters."
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function RejectsBrowser({ search }: { search: BrowseSearch }) {
  const navigate = useNavigate();
  const { data, error, isLoading } = useApi(evidence.rejectBrowse, {
    query: {
      limit: 200,
      standing: search.standing,
      q: search.q || undefined,
    },
  });
  const rejects = data?.rejects ?? [];
  const summary = data?.summary;
  const selected = search.reject;

  const columns: DataTableColumn<EvidenceRejectBrowseRowDto>[] = [
    {
      key: "task",
      header: "Task",
      sort: (a, b) => a.taskId - b.taskId,
      render: (r) => (
        <span>
          <span className={NUM}>#{r.taskId}</span>{" "}
          <span className="line-clamp-1">{r.taskTitle ?? "—"}</span>
        </span>
      ),
    },
    {
      key: "bounce",
      header: "Bounce",
      sort: (a, b) => a.bounce - b.bounce,
      render: (r) => <span className={NUM}>{r.bounce}</span>,
    },
    {
      key: "verifier",
      header: "Verifier",
      render: (r) => <code className="type-subtext">{r.verifier}</code>,
    },
    {
      key: "note",
      header: "Note",
      render: (r) => (
        <span className="type-body line-clamp-2" title={r.note}>
          {r.note.trim() || "—"}
        </span>
      ),
    },
    {
      key: "next",
      header: "What next",
      render: (r) => (
        <IntentBadge intent={nextIntent(r.nextKind)}>{nextLabel(r.nextKind)}</IntentBadge>
      ),
    },
    {
      key: "when",
      header: "When",
      sort: (a, b) => a.rejectedAt.localeCompare(b.rejectedAt),
      render: (r) => (
        <span className={MUTED}>{formatRelativeTime(asIso(r.rejectedAt))}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-4">
        <StatCard title="Rejects" value={String(summary?.total ?? "…")} />
        <StatCard
          title="Standing"
          value={String(summary?.standing ?? "…")}
          description="Unanswered — still no"
          leadingDot="danger"
        />
        <StatCard
          title="Reworked"
          value={String(summary?.reworked ?? "…")}
          leadingDot="success"
        />
        <StatCard
          title="Superseded"
          value={String(summary?.superseded ?? "…")}
          leadingDot="neutral"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          href={browseHref({ tab: "rejects", q: search.q })}
          active={!search.standing}
        >
          All
        </FilterChip>
        <FilterChip
          href={browseHref({ tab: "rejects", standing: "yes", q: search.q })}
          active={search.standing === "yes"}
        >
          Standing
        </FilterChip>
        <FilterChip
          href={browseHref({ tab: "rejects", standing: "no", q: search.q })}
          active={search.standing === "no"}
        >
          Answered
        </FilterChip>
      </div>

      {selected ? <RejectDetailCard opsId={selected} search={search} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>Review verdicts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && !data ? (
            <p className="type-body text-muted-foreground">Loading review.rejected ledger…</p>
          ) : error ? (
            <p className="type-body text-destructive">
              {error instanceof Error ? error.message : String(error)}
            </p>
          ) : (
            <DataTable
              rows={rejects}
              columns={columns}
              rowKey={(r) => r.opsId}
              wrapInCard={false}
              empty="No review.rejected rows match these filters."
              onRowClick={(r) => {
                navigate.push(
                  browseHref({
                    tab: "rejects",
                    standing: search.standing,
                    reject: r.opsId,
                    q: search.q,
                  }),
                );
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RejectDetailCard({
  opsId,
  search,
}: {
  opsId: number;
  search: BrowseSearch;
}) {
  const { data, error, isLoading } = useApi(evidence.rejectDetail, {
    params: { id: opsId },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rejection detail</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && !data ? (
          <p className="type-body text-muted-foreground">Loading rejection #{opsId}…</p>
        ) : error ? (
          <p className="type-body text-destructive">
            {error instanceof Error ? error.message : String(error)}
          </p>
        ) : data ? (
          <RejectDetailBody data={data} search={search} />
        ) : null}
      </CardContent>
    </Card>
  );
}

function RejectDetailBody({
  data,
  search,
}: {
  data: EvidenceRejectDetailDto;
  search: BrowseSearch;
}) {
  const next = data.next;
  return (
    <div className="space-y-4">
      <dl className="grid gap-3 type-body sm:grid-cols-2">
        <div>
          <dt className={MUTED}>What</dt>
          <dd>
            <Link
              href={`/agents/verification/${data.taskId}`}
              className="text-primary hover:underline"
            >
              #{data.taskId} {data.taskTitle ?? ""}
            </Link>
            <span className={`ml-2 ${MUTED}`}>{data.taskStatus ?? "—"}</span>
          </dd>
        </div>
        <div>
          <dt className={MUTED}>By whom</dt>
          <dd>
            <code className="type-subtext">{data.verifier}</code>
            <span className={`ml-2 ${MUTED}`}>
              bounced {data.shipper ?? "unclaimed"} · bounce {data.bounce}
            </span>
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className={MUTED}>Note</dt>
          <dd className="whitespace-pre-wrap">{data.note.trim() || "—"}</dd>
        </div>
        <div>
          <dt className={MUTED}>When</dt>
          <dd>{data.rejectedAt ? formatDateTime(asIso(data.rejectedAt)) : "—"}</dd>
        </div>
        <div>
          <dt className={MUTED}>What happened next</dt>
          <dd>
            <IntentBadge intent={nextIntent(next.kind)}>{nextLabel(next.kind)}</IntentBadge>
          </dd>
        </div>
      </dl>

      <dl className="grid gap-3 type-body sm:grid-cols-3">
        <div>
          <dt className={MUTED}>Rework</dt>
          <dd>
            {next.reworked
              ? "New linked commit after this reject"
              : next.noRework
                ? "Re-shipped with no new commit"
                : next.reShipped
                  ? "Re-shipped"
                  : "No re-ship yet"}
          </dd>
        </div>
        <div>
          <dt className={MUTED}>Supersession</dt>
          <dd>
            {next.supersededBy
              ? `${next.supersededBy.verifier ?? "verifier"} named this reject and replaced it`
              : next.superseded
                ? "Superseded"
                : "Not superseded"}
          </dd>
        </div>
        <div>
          <dt className={MUTED}>Re-audit count</dt>
          <dd className={NUM}>
            {next.reAuditCount} DoD retries · {next.laterRejects} later rejects ·{" "}
            {next.laterReceipts} later receipts
          </dd>
        </div>
      </dl>

      {next.nextVerdict ? (
        <p className="type-body">
          Next judgment:{" "}
          <IntentBadge intent={next.nextVerdict.verdict === "reject" ? "danger" : "success"}>
            {next.nextVerdict.verdict}
          </IntentBadge>{" "}
          by <code className="type-subtext">{next.nextVerdict.verifier ?? "—"}</code>{" "}
          <span className={MUTED}>
            {formatRelativeTime(asIso(next.nextVerdict.createdAt))}
          </span>
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm">
          <Link href={`/agents/verification/${data.taskId}`}>Open task evidence</Link>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link
            href={browseHref({
              tab: "rejects",
              standing: search.standing,
              q: search.q,
            })}
          >
            Close detail
          </Link>
        </Button>
      </div>
    </div>
  );
}
